import React, { useEffect, useState, useRef } from "react";
import Prismic from "@prismicio/client";
import { Modal } from "react-responsive-modal";
import "react-responsive-modal/styles.css";
import { Link } from "react-router-dom";
import Lottie from "lottie-react";
import NDA from "../Assets/NDA.json";
import New from "../Assets/new.json";

function MultiCaseOptionCard({ link, fallbackUrl, title, description, image, onNavigate }) {
  const isDocument = link?.link_type === "Document" && link?.uid;
  if (!isDocument && !fallbackUrl) return null;

  const cardContent = (
    <>
      <img className="multi-case-card-image" src={image} alt={title} />
      <div className="multi-case-card-footer">
        <div className="multi-case-card-tag">
          <span className="multi-case-card-dot" />
          <span>{title}</span>
        </div>
        <h3 className="multi-case-card-heading">{description}</h3>
      </div>
    </>
  );

  return isDocument ? (
    // Content relationship (linked document): open the internal case-study page
    <Link
      to={`/case-study/${link.uid}`}
      className="multi-case-card"
      onClick={onNavigate}
    >
      {cardContent}
    </Link>
  ) : (
    // Not a content relationship: fall back to the plain link_1/link_2 URL
    <a
      href={fallbackUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="multi-case-card"
      onClick={onNavigate}
    >
      {cardContent}
    </a>
  );
}

export default function OtherWork({ doc: docProp } = {}) {
  //Multi case-study picker modal
  const [isCaseModalOpen, setIsCaseModalOpen] = useState(false);
  const [activeWorkItem, setActiveWorkItem] = useState(null);

  const openMultiCaseModal = (workItem) => {
    setActiveWorkItem(workItem);
    setIsCaseModalOpen(true);
  };

  const closeMultiCaseModal = () => {
    setIsCaseModalOpen(false);
    setActiveWorkItem(null);
  };

  useEffect(() => {
    document.body.style.overflow = isCaseModalOpen ? "hidden" : "";
    document.documentElement.style.overflow = isCaseModalOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    };
  }, [isCaseModalOpen]);

  // "View Case Study" cursor pill: follows the mouse via direct DOM updates
  // (not React state) so it doesn't trigger a re-render on every mousemove.
  const cursorPillRef = useRef(null);
  const [isCursorPillVisible, setIsCursorPillVisible] = useState(false);

  const handleWorkMouseMove = (evt) => {
    if (cursorPillRef.current) {
      cursorPillRef.current.style.transform = `translate(${evt.clientX}px, ${evt.clientY}px) translate(-50%, -50%)`;
    }
  };

  // mouseleave never fires when a card scrolls out from under a stationary
  // cursor — only actual pointer movement triggers it. Hide on scroll too.
  useEffect(() => {
    const hidePillOnScroll = () => setIsCursorPillVisible(false);
    window.addEventListener("scroll", hidePillOnScroll, { passive: true });
    return () => window.removeEventListener("scroll", hidePillOnScroll);
  }, []);

  const apiEndpoint = "https://harshitsharma.prismic.io/api/v2";
  const accessToken =
    "MC5ZYUVtbXhNQUFETUFuODZN.77-977-9Ye-_ve-_vX8o77-977-977-977-977-9a--_ve-_ve-_ve-_ve-_ve-_vQITBF5h77-977-977-977-977-977-9TO-_vQ";
  // This is where you would add your access token for a Private repository
  const Client = Prismic.client(apiEndpoint, { accessToken });
  // A parent (e.g. the home page) is managing this fetch if `doc` was passed
  // at all — even while it's still `null`/loading. Only self-fetch when no
  // parent is providing it (e.g. standalone on the /work route).
  const hasDocProp = docProp !== undefined;
  const [fetchedDoc, setFetchedDoc] = useState(null);
  const [ownIsLoading, setOwnIsLoading] = useState(true);
  const doc = hasDocProp ? docProp : fetchedDoc;
  const isWorkLoading = hasDocProp ? docProp === null : ownIsLoading;

  useEffect(() => {
    // If a parent already fetched this (e.g. the home page), don't fetch again.
    if (hasDocProp) return;

    const fetchData = async () => {
      const response = await Client.query(
        Prismic.Predicates.at("document.type", "morework"),
        { orderings: "[my.morework.order desc]" },
      );
      if (response) {
        setFetchedDoc(response.results);
      }
      setOwnIsLoading(false);
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasDocProp]);

  return (
    <div className="container">
  <div
    ref={cursorPillRef}
    className={`work-cursor-pill ${isCursorPillVisible ? "visible" : ""}`}
  >
    View Case Study
  </div>

  <div className="container-div1">
    <h1>Client Projects</h1>
    <p>
      Below projects showcase my EDPIT design process, iterations, final
      product, including results & achievements.
    </p>
  </div>

  <div className="more-work-section">
    <div className="more-work-grid" onMouseMove={handleWorkMouseMove}>
      {isWorkLoading &&
        Array.from({ length: 3 }).map((_, i) => (
          <div className="work-card" key={`skeleton-${i}`}>
            <div className="skeleton" style={{ width: "100%", height: 250 }} />
            <div className="work-card-header">
              <div className="work-card-header-left">
                <div className="skeleton skeleton-circle" style={{ width: 12, height: 12 }} />
                <div className="skeleton skeleton-text" style={{ width: "60%" }} />
              </div>
            </div>
            <div className="work-card-description">
              <div className="skeleton skeleton-text" style={{ width: "90%", marginBottom: 6 }} />
              <div className="skeleton skeleton-text" style={{ width: "70%" }} />
            </div>
          </div>
        ))}

      {!isWorkLoading &&
        doc?.map((e) => {
        const multi = e?.data?.multi_case_study?.[0];

        return (
        <div
          className="work-card"
          key={e?.id || e?.data?.title}
          onMouseEnter={() => setIsCursorPillVisible(true)}
          onMouseLeave={() => setIsCursorPillVisible(false)}
        >
          {e?.data.nda && (
            <Lottie className="nda" animationData={NDA} loop={true} />
          )}
          {e?.data.new && (
            <Lottie className="nda" animationData={New} loop={true} />
          )}

          {e?.data.nda ? (
            <Link to={``}>
              <img
                className="work-card-image"
                src={e?.data.images[0].image_link}
                alt={e?.data.title || ""}
              />

               <div className="work-header">
                  <div className="work-header-left">
                    <div className="work-block" />
                    <h1 className="work-title">{e?.data?.title}</h1>
                  </div>
                  <span className="work-year">{e.project_image || "2024"}</span>
                </div>
              <div className="work-card-description">
                <p>{e?.data.small_description}</p>
              </div>
            </Link>
          ) : multi?.enable ? (
            // Multiple case studies: opens a picker modal instead of navigating directly
            <div
              role="button"
              tabIndex={0}
              className="work-multi-trigger"
              onClick={() => openMultiCaseModal(e)}
              onKeyDown={(evt) => {
                if (evt.key === "Enter" || evt.key === " ") {
                  openMultiCaseModal(e);
                }
              }}
            >
              <img
                className="work-card-image"
                src={e?.data.images[0].image_link}
                alt={e?.data.title || ""}
              />

              <div className="work-card-header">
                <div className="work-card-header-left">
                  <div className="work-card-block" />
                  <h1 className="work-card-title">{e?.data.title}</h1>
                </div>
                <span className="work-year">→ {e.data.date} ←</span>
              </div>

              <div className="work-card-description">
                <p>{e?.data.small_description}</p>
              </div>
            </div>
          ) : (
            <Link to={`/case-study/${e?.data.content_relationship.uid}`}>
              <img
                className="work-card-image"
                src={e?.data.images[0].image_link}
                alt={e?.data.title || ""}
              />

              <div className="work-card-header">
                <div className="work-card-header-left">
                  <div className="work-card-block" />
                  <h1 className="work-card-title">{e?.data.title}</h1>
                </div>
                <span className="work-year">→ {e.data.date} ←</span>
              </div>

              <div className="work-card-description">
                <p>{e?.data.small_description}</p>

              </div>
            </Link>
          )}
        </div>
        );
      })}
    </div>
  </div>

  <Modal
    open={isCaseModalOpen}
    onClose={closeMultiCaseModal}
    center
    showCloseIcon={false}
    blockScroll={false}
    classNames={{
      modal: "multi-case-modal",
      modalAnimationIn: "multi-case-slide-in",
      modalAnimationOut: "multi-case-slide-out",
    }}
  >
    <div className="multi-case-modal-topbar">
      <h2 className="multi-case-modal-heading">Choose case study to view</h2>
      <button
        type="button"
        className="multi-case-modal-close"
        onClick={closeMultiCaseModal}
      >
        <span className="multi-case-modal-close-dot" />
        Close
      </button>
    </div>
    <div className="multi-case-modal-grid">
      {(() => {
        const multi = activeWorkItem?.data?.multi_case_study?.[0];
        return (
          <>
            <MultiCaseOptionCard
              link={multi?.content_relationship_1}
              fallbackUrl={multi?.link_1}
              title={multi?.title_1}
              description={multi?.description_1}
              image={multi?.image_1}
              onNavigate={closeMultiCaseModal}
            />
            <MultiCaseOptionCard
              link={multi?.content_relationshiop_2}
              fallbackUrl={multi?.link_2}
              title={multi?.title_2}
              description={multi?.description_2}
              image={multi?.image_2}
              onNavigate={closeMultiCaseModal}
            />
          </>
        );
      })()}
    </div>
  </Modal>
</div>
  );
}
